import { Metadata } from 'next';
import { COMPANY_DEFAULTS } from './constants/companyInfo';

// Site-wide SEO configuration
export const siteConfig = {
  name: COMPANY_DEFAULTS.name,
  domain: process.env.NEXT_PUBLIC_DOMAIN || '',
  url: COMPANY_DEFAULTS.siteUrl,
  description: 'Premium custom window blinds, shades, and shutters. Get expert help with measuring, installation, and find the perfect window treatments for your home or business.',
  keywords: [
    // Core Product Keywords
    'blinds',
    'curtains',
    'custom blinds',
    'window blinds',
    'window shades',
    'roller shades',
    'cellular shades',
    'roman shades',
    'motorized blinds',
    'smart blinds',
    'faux wood blinds',
    'wood blinds',
    'venetian blinds',
    'vertical blinds',
    'window treatments',
    'window coverings',
    'custom window treatments',
    'blinds online',
    'shades online',
    'buy blinds',
    'cheap blinds',
    'affordable blinds',
    'energy efficient blinds',
    'blackout blinds',
    'light filtering shades',

    // Product Features (High Priority - Competitor Gap)
    'cordless blinds',
    'no-drill blinds',
    'eco-friendly blinds',
    'sustainable blinds',
    'zebra shades',
    'outdoor shades',
    'patio blinds',
    'quick ship blinds',

    // Service Keywords (Missing from Current)
    'free samples',
    'professional installation',
    'perfect fit guarantee',
    'virtual design consultant',
    'custom window blinds',

    // Long-tail Keywords (High Intent)
    'blinds for windows',
    'blinds for sliding glass doors',
    'blinds for bay windows',
    'blackout blinds for nursery',
    'energy efficient cellular shades',
    'blinds for large windows',
    'best blinds for bedroom',
    'kitchen window blinds',
    'bathroom moisture resistant blinds',

    // 2026 Trends (Competitive Advantage)
    'allusion blinds',
    'cassette roller blinds',
    'day night blinds',
    'smart home blinds',
    'motorized window treatments',
    'automated blinds',
    'voice controlled blinds',

    // Additional High-Value
    'honeycomb shades',
    'plantation shutters',
    'solar shades',
    'top down bottom up shades',
    'blackout roller shades',
    'room darkening shades',

    // Commercial & Transactional Keywords
    'cheap blinds online',
    'best blinds 2026',
    'blinds free shipping',
    'blinds sale',
    'blinds clearance',
    'blinds coupon code',
    'blinds discount',
    'blackout curtains',
    'privacy shades',
    'blinds for renters',
    'apartment blinds',
    'RV blinds',
    'office blinds',
    'blinds warranty',
    'blinds reviews',
    'blinds comparison',
    'free blind samples',
    'window blind installation service',
  ],
  // Fallback values: seo-config.ts is not a React component so it cannot use useSiteConfig().
  // In client components, prefer useSiteConfig().companyName instead of these static defaults.
  // These are used by generatePageMetadata / defaultMetadata which run at build/server time.
  author: COMPANY_DEFAULTS.name,
  creator: COMPANY_DEFAULTS.name,
  publisher: COMPANY_DEFAULTS.name,
  locale: 'en_US',
  type: 'website',
  twitterHandle: COMPANY_DEFAULTS.social.twitter,
  phone: COMPANY_DEFAULTS.phone,
  email: COMPANY_DEFAULTS.email.support,
  address: {
    streetAddress: 'P.O. Box 166',
    addressLocality: 'Kirkland',
    addressRegion: 'WA',
    postalCode: '98083',
    addressCountry: 'US',
  },
  logo: '/images/logo/logo-charcoal-v3.png',
  ogImage: '/og?title=Smart%20Blinds%20Pro&subtitle=Premium%20Custom%20Window%20Treatments',
  // Helper function to generate dynamic OG image URL
  getOgImageUrl: (title: string, subtitle?: string, type?: 'default' | 'product' | 'guide' | 'sale') => {
    const params = new URLSearchParams();
    params.set('title', title);
    if (subtitle) params.set('subtitle', subtitle);
    if (type) params.set('type', type);
    return `/og?${params.toString()}`;
  },
};

// Default metadata for the site
export const defaultMetadata: Metadata = {
  metadataBase: new URL(siteConfig.url),
  title: {
    default: `${siteConfig.name} - Premium Custom Window Blinds & Shades`,
    template: `%s | ${siteConfig.name}`,
  },
  description: siteConfig.description,
  // `keywords` deliberately NOT emitted site-wide. Google has ignored the meta
  // keywords tag since 2009, and Bing treats a stuffed one as a spam signal —
  // this dumped all 78 entries of siteConfig.keywords into a 1,443-character
  // tag on every page. The array itself is kept: it still feeds internal
  // keyword tooling and the per-page helper below (capped at 10 + page terms).
  authors: [{ name: siteConfig.author, url: siteConfig.url }],
  creator: siteConfig.creator,
  publisher: siteConfig.publisher,
  formatDetection: {
    email: false,
    address: false,
    telephone: false,
  },
  openGraph: {
    type: 'website',
    locale: siteConfig.locale,
    url: siteConfig.url,
    siteName: siteConfig.name,
    title: `${siteConfig.name} - Premium Custom Window Blinds & Shades`,
    description: siteConfig.description,
    images: [
      {
        url: `${siteConfig.url}${siteConfig.ogImage}`,
        width: 1200,
        height: 630,
        alt: `${siteConfig.name} - Custom Window Treatments`,
      },
    ],
  },
  twitter: {
    card: 'summary_large_image',
    title: `${siteConfig.name} - Premium Custom Window Blinds & Shades`,
    description: siteConfig.description,
    site: siteConfig.twitterHandle,
    creator: siteConfig.twitterHandle,
    images: [`${siteConfig.url}${siteConfig.ogImage}`],
  },
  robots: {
    index: true,
    follow: true,
    nocache: false,
    googleBot: {
      index: true,
      follow: true,
      noimageindex: false,
      'max-video-preview': -1,
      'max-image-preview': 'large',
      'max-snippet': -1,
    },
  },
  verification: {
    // Add your verification codes here
    google: process.env.GOOGLE_SITE_VERIFICATION || '',
    yandex: process.env.YANDEX_VERIFICATION || '',
    // bing: process.env.BING_VERIFICATION || '',
  },
  category: 'Home Improvement',
};

// Generate page-specific metadata
export function generatePageMetadata({
  title,
  description,
  keywords = [],
  path = '',
  image,
  noIndex = false,
}: {
  title: string;
  description: string;
  keywords?: string[];
  path?: string;
  image?: string;
  noIndex?: boolean;
}): Metadata {
  const url = `${siteConfig.url}${path}`;
  const ogImage = image || siteConfig.ogImage;

  return {
    title,
    description,
    keywords: [...siteConfig.keywords.slice(0, 10), ...keywords],
    openGraph: {
      title: `${title} | ${siteConfig.name}`,
      description,
      url,
      siteName: siteConfig.name,
      images: [
        {
          url: ogImage.startsWith('http') ? ogImage : `${siteConfig.url}${ogImage}`,
          width: 1200,
          height: 630,
          alt: title,
        },
      ],
      locale: siteConfig.locale,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title: `${title} | ${siteConfig.name}`,
      description,
      images: [ogImage.startsWith('http') ? ogImage : `${siteConfig.url}${ogImage}`],
    },
    // Relative canonical — the root layout sets tenant-aware metadataBase, so
    // Next.js absolutizes this to the correct domain per tenant. Previously
    // we hardcoded smartblindspro.com, which forced every tenant's pages to
    // canonicalize to smartblindspro.com (bad for the secondary tenants' SEO).
    alternates: {
      canonical: path || '/',
    },
    robots: noIndex
      ? { index: false, follow: false }
      : { index: true, follow: true },
  };
}

// Generate product metadata
export function generateProductMetadata({
  name,
  description,
  slug,
  image,
  price,
  category,
  rating,
  reviewCount,
}: {
  name: string;
  description: string;
  slug: string;
  image?: string;
  price: number;
  category?: string;
  rating?: number;
  reviewCount?: number;
}): Metadata {
  const path = `/products/configure/${slug}`;
  const url = `${siteConfig.url}${path}`;
  const productTitle = `${name} - Custom ${category || 'Window Treatment'}`;
  const productDescription = description || `Shop ${name} at ${siteConfig.name}. ${category ? `Premium ${category} ` : ''}starting at $${price.toFixed(2)}. Free shipping.`;

  const keywords = [
    name.toLowerCase(),
    `buy ${name.toLowerCase()}`,
    `${name.toLowerCase()} online`,
    category?.toLowerCase() || '',
    'custom blinds',
    'window treatments',
  ].filter(Boolean);

  return {
    title: productTitle,
    description: productDescription.slice(0, 160),
    keywords,
    openGraph: {
      title: productTitle,
      description: productDescription.slice(0, 160),
      url,
      siteName: siteConfig.name,
      images: image
        ? [
            {
              url: image.startsWith('http') ? image : `${siteConfig.url}${image}`,
              width: 800,
              height: 600,
              alt: name,
            },
          ]
        : undefined,
      locale: siteConfig.locale,
      type: 'website',
    },
    twitter: {
      card: 'summary_large_image',
      title: productTitle,
      description: productDescription.slice(0, 160),
      images: image ? [image.startsWith('http') ? image : `${siteConfig.url}${image}`] : undefined,
    },
    // Relative canonical — see generatePageMetadata for rationale
    alternates: {
      canonical: path,
    },
  };
}

// Generate category metadata
export function generateCategoryMetadata({
  name,
  description,
  slug,
  image,
}: {
  name: string;
  description?: string;
  slug: string;
  image?: string;
}): Metadata {
  const path = `/products?category=${slug}`;
  const url = `${siteConfig.url}${path}`;
  const categoryTitle = `${name} - Shop Custom ${name}`;
  const categoryDescription = description || `Browse our selection of custom ${name}. Find the perfect ${name.toLowerCase()} for your home with Free shipping.`;

  return {
    title: categoryTitle,
    description: categoryDescription.slice(0, 160),
    keywords: [
      name.toLowerCase(),
      `custom ${name.toLowerCase()}`,
      `buy ${name.toLowerCase()}`,
      `${name.toLowerCase()} online`,
      'window treatments',
    ],
    openGraph: {
      title: `${categoryTitle} | ${siteConfig.name}`,
      description: categoryDescription.slice(0, 160),
      url,
      siteName: siteConfig.name,
      images: image
        ? [
            {
              url: image.startsWith('http') ? image : `${siteConfig.url}${image}`,
              width: 1200,
              height: 630,
              alt: `${name} Collection`,
            },
          ]
        : undefined,
      locale: siteConfig.locale,
      type: 'website',
    },
    // Relative canonical — see generatePageMetadata for rationale
    alternates: {
      canonical: path,
    },
  };
}
